import React, { useEffect, useState } from "react";
import FormModal from "../../../components/forms/FormModal";
import { getTimeOffTypes, createTimeOffRequest } from "../../../services/timeOffService";
import * as peopleService from "../../../services/peopleService";
import { usePermissions } from "../../../hooks/usePermission";
import { useAuth } from "../../../context/AuthContext";

// Request leave (or schedule it for someone, if you can approve it) in the shared form modal.
export default function LeaveModal({ onClose, onSaved }) {
  const { user } = useAuth();
  const { canApproveTimeOff } = usePermissions();
  const [types, setTypes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: user.user_id, time_off_type_id: "", start_date: "", end_date: "", reason: "", status: canApproveTimeOff ? "approved" : "pending" });
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    (async () => {
      try {
        const loaded = await getTimeOffTypes();
        setTypes(loaded);
        setForm((prev) => ({ ...prev, time_off_type_id: String(loaded[0]?.leave_type_id ?? "") }));
        if (canApproveTimeOff) {
          const data = await peopleService.getEmployees();
          setEmployees(data.employees || data);
        }
      } catch (err) {
        console.error("Error loading time off data:", err);
        setError("Failed to load time off data");
      }
    })();
  }, [canApproveTimeOff]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await createTimeOffRequest(form);
      onSaved();
    } catch (err) {
      console.error("Error creating time off request:", err);
      setError(err.response?.data?.error || "Failed to create time off request");
    }
  };

  const person = (emp) => emp.name + (emp.job_title ? ` (${emp.job_title})` : "");

  return (
    <FormModal
      title={canApproveTimeOff ? "Schedule Leave" : "Request Leave"}
      fields={[
        {
          key: "employee_id",
          label: "Person/Resource",
          type: "select",
          required: true,
          value: form.employee_id,
          onChange: set("employee_id"),
          options: canApproveTimeOff
            ? [{ value: "", label: "Select employee..." }, ...employees.map((emp) => ({ value: emp.user_id, label: person(emp) }))]
            : [{ value: user.user_id, label: person(user) }],
        },
        {
          key: "time_off_type_id",
          label: "Leave Type",
          type: "select",
          required: true,
          value: form.time_off_type_id,
          onChange: set("time_off_type_id"),
          options: types.map((type) => ({ value: String(type.leave_type_id || type.id || "unknown"), label: type.name || "Unnamed" })),
        },
        {
          key: "dates",
          label: "Leave Dates",
          type: "custom",
          render: () => (
            <div style={{ display: "flex", gap: "16px" }}>
              {[["start_date", "Start Date"], ["end_date", "End Date"]].map(([key, label]) => (
                <div key={key} style={{ flex: 1 }}>
                  <label style={{ display: "block", marginBottom: "4px", fontSize: "0.85rem", color: "#6b7280" }}>{label}</label>
                  <input type="date" required value={form[key]} onChange={set(key)} style={{ width: "100%" }} />
                </div>
              ))}
            </div>
          ),
        },
        {
          key: "reason",
          label: "Details (Optional)",
          type: "textarea",
          value: form.reason,
          onChange: set("reason"),
          placeholder: "Please provide a reason for your leave request...",
        },
      ]}
      onSubmit={submit}
      onCancel={onClose}
      error={error}
    />
  );
}
